import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import { Button, Chip, OutlinedInput, TextField, Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useEffect, useState } from "react";
import { TableColumn } from "react-data-table-component";
import { useLocation, useNavigate } from "react-router-dom";
import AlertDialog from "../../../../ui/components/alert-dialog/alert-dialog";
import AlertMessage from "../../../../ui/components/alert-message/alert-message";
import AutocompleteSelect from "../../../../ui/components/autocomplete-select/autocomplete-select";
import Table from "../../../../ui/components/table/table";
import { ListModel } from "../../../models/list-model";
import { ProfesionalAdminList } from "../../../models/profesional-list";
import { ProfesionalStatus } from "../../../models/profesional-status";
import { profesionalServices } from "../../../services/profesional.services";
import {
  initialFilter,
  queryParamsConfig,
} from "../../../utils/profesional-admin.utils";
import { useQueryParamsReplace } from "../../../utils/useQueryParamsReplace";
import "./profesionales-list.scss";

const ESTADOS: ListModel[] = [
  { value: "true", label: "Activos" },
  { value: "false", label: "Inactivos" },
];

export default function ProfesionalesList() {
  const [queryParams, setQueryParams] =
    useQueryParamsReplace(queryParamsConfig);
  const [fetching, setFetching] = useState<boolean>(true);
  const [entities, setEntities] = useState<ProfesionalAdminList[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const [showAlertMessage, setShowAlertMessage] = useState<boolean>(false);
  const [profesionalToToggle, setProfesionalToToggle] =
    useState<ProfesionalAdminList | null>(null);
  const [toggling, setToggling] = useState(false);
  const [observacionNota, setObservacionNota] = useState("");
  const [loadingObservacion, setLoadingObservacion] = useState(false);

  useEffect(() => {
    if (location.search === "") {
      setQueryParams(initialFilter);
    }
    if (queryParams.page) {
      getProfesionales();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  useEffect(() => {
    if (state?.textAlert) setShowAlertMessage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const getProfesionales = async () => {
    setFetching(true);
    profesionalServices
      .getList(queryParams)
      .then((response: any) => {
        setFetching(false);
        setEntities(response.data.items);
        setTotalCount(response.data.totalItems);
      })
      .catch(() => setFetching(false));
  };

  const goToCreate = () => navigate("nuevo");
  const goToEdit = (row: ProfesionalAdminList) => navigate(`${row.id}`);

  const openToggleDialog = (row: ProfesionalAdminList) => {
    setProfesionalToToggle(row);
    setObservacionNota("");
    setLoadingObservacion(true);
    profesionalServices
      .getFicha(row.id)
      .then(({ data }) => setObservacionNota(data.observacion ?? ""))
      .catch(() => {})
      .finally(() => setLoadingObservacion(false));
  };

  const confirmToggleActive = () => {
    if (!profesionalToToggle) return;
    setToggling(true);
    const action = profesionalToToggle.active
      ? profesionalServices.desactivar
      : profesionalServices.activar;
    action(profesionalToToggle.id, observacionNota || undefined)
      .then(({ data }: { data: ProfesionalStatus }) => {
        setEntities((prev) =>
          prev.map((p) =>
            p.id === data.id ? { ...p, active: data.active } : p
          )
        );
      })
      .finally(() => {
        setToggling(false);
        setProfesionalToToggle(null);
        setObservacionNota("");
      });
  };

  const closeToggleDialog = () => {
    setProfesionalToToggle(null);
    setObservacionNota("");
  };

  const columns: TableColumn<ProfesionalAdminList>[] = [
    {
      name: "Apellido",
      selector: (row) => row.apellido,
      sortable: true,
      sortField: "apellido",
    },
    {
      name: "Nombre",
      selector: (row) => row.nombre,
      sortable: true,
      sortField: "nombre",
    },
    {
      name: "DNI",
      selector: (row) => row.dni,
    },
    {
      name: "CUIT",
      selector: (row) => row.cuit,
    },
    {
      name: "Matrícula",
      selector: (row) => row.matriculaProf,
    },
    {
      name: "Localidad",
      selector: (row) => row.localidadLaboral,
      wrap: true,
    },
    {
      name: "Estado",
      cell: (row) => (
        <Chip
          label={row.active ? "ACTIVO" : "INACTIVO"}
          color={row.active ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      name: "",
      button: true,
      cell: (row) => (
        <Tooltip title={row.active ? "Dar de baja" : "Activar"}>
          <IconButton
            aria-label={row.active ? "dar-de-baja" : "activar"}
            onClick={() => openToggleDialog(row)}
          >
            {row.active ? (
              <ToggleOnOutlinedIcon color="success" />
            ) : (
              <ToggleOffOutlinedIcon color="disabled" />
            )}
          </IconButton>
        </Tooltip>
      ),
      width: "5%",
    },
    {
      name: "",
      button: true,
      cell: (row) => (
        <Tooltip title="Ver / Editar">
          <IconButton aria-label="editar" onClick={() => goToEdit(row)}>
            <EditOutlinedIcon />
          </IconButton>
        </Tooltip>
      ),
      width: "5%",
    },
  ];

  return (
    <div className="profesionales-list-container">
      {state?.textAlert && (
        <AlertMessage
          text={state.textAlert}
          type="success"
          show={showAlertMessage}
          setShow={setShowAlertMessage}
          handleShowExternaly
          timeClose={4000}
        ></AlertMessage>
      )}
      <div className="row action-button">
        <OutlinedInput
          type="text"
          placeholder="Nombre, apellido, DNI o CUIT"
          className="custom-input-search"
          autoComplete="off"
          onChange={(event) => {
            setQueryParams({
              ...queryParams,
              search: event.target.value !== "" ? event.target.value : undefined,
            });
          }}
        ></OutlinedInput>
        <AutocompleteSelect
          name="active"
          label="Estado"
          isMultiple={false}
          options={ESTADOS}
          value={queryParams.active ?? ""}
          onChange={(value) => {
            setQueryParams({
              ...queryParams,
              active: value !== "" ? value : undefined,
            });
          }}
        ></AutocompleteSelect>
        <Button className="button" variant="contained" onClick={goToCreate}>
          Nuevo Profesional
        </Button>
      </div>
      <div className="row">
        <div className="profesionales-table">
          <Table
            table={{
              columns,
              data: entities,
              paginationTotalRows: totalCount,
              pointerOnHover: true,
              highlightOnHover: true,
              onRowClicked: goToEdit,
            }}
            setQueryParams={setQueryParams}
            queryParams={queryParams}
          ></Table>
        </div>
      </div>

      <AlertDialog
        show={!!profesionalToToggle}
        setShow={(show) => {
          if (!show) closeToggleDialog();
        }}
        title={
          profesionalToToggle?.active
            ? "Dar de baja profesional"
            : "Activar profesional"
        }
        text={
          profesionalToToggle?.active
            ? `¿Confirma que desea dar de baja la matrícula de ${profesionalToToggle?.nombre} ${profesionalToToggle?.apellido}? Podrá reactivarla más adelante.`
            : `¿Confirma que desea reactivar la matrícula de ${profesionalToToggle?.nombre} ${profesionalToToggle?.apellido}?`
        }
        children={
          <TextField
            label="Observación"
            multiline
            rows={3}
            fullWidth
            value={observacionNota}
            onChange={(e) => setObservacionNota(e.target.value)}
            disabled={loadingObservacion}
            placeholder={loadingObservacion ? "Cargando..." : ""}
            inputProps={{ maxLength: 500 }}
            sx={{ mt: 2 }}
          />
        }
        actions={
          <>
            <Button onClick={closeToggleDialog}>Cancelar</Button>
            <Button
              variant="contained"
              color={profesionalToToggle?.active ? "error" : "success"}
              disabled={toggling}
              onClick={confirmToggleActive}
            >
              Confirmar
            </Button>
          </>
        }
      ></AlertDialog>
    </div>
  );
}
